(() => {
  'use strict';

  const configNode =
    document.getElementById(
      'SmartMakroFreeGiftConfig'
    );

  if (!configNode) return;

  let config;

  try {
    config =
      JSON.parse(
        configNode.textContent
      );
  } catch (error) {
    console.error(
      'Smart Makro Free Gift: config kunne ikke læses.',
      error
    );

    return;
  }

  if (
    !config.enabled ||
    !Array.isArray(config.giftProducts) ||
    !config.giftProducts.length
  ) {
    return;
  }

  const root =
    window.Shopify?.routes?.root ||
    '/';


  const state = {
    cart: null,
    loading: false,
    autoRemoving: false,
    error: '',
    activeGiftId: null,

    infoGiftId: null,
    infoImageIndex: 0,

    /*
      false = gavevælger kollapser efter valg.
      true = kunden har åbnet den igen.
    */
    giftPickerExpanded: false
  };


  const esc =
    value =>
      String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');


  const formatGiftTitle =
    value =>
      esc(value)
        .replace(
          /motivationsvandflaske/gi,
          'motivations&shy;vandflaske'
        )
        .replace(
          /motivationsnotesbog/gi,
          'motivations&shy;notesbog'
        );


  const money = (
    cents,
    currency = 'DKK'
  ) => {
    try {
      return new Intl.NumberFormat(
        document.documentElement.lang ||
        'da-DK',
        {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }
      ).format(
        cents / 100
      );
    } catch (_) {
      return `${(cents / 100)
        .toFixed(2)
        .replace('.', ',')} kr.`;
    }
  };


  const choicesFor =
    gift =>
      Array.isArray(
        gift?.choices
      )
        ? gift.choices
        : [];


  const availableChoices =
    gift =>
      choicesFor(gift)
        .filter(
          choice =>
            choice.available
        );


  const strictPhysicalChoice =
    gift => {
      const available =
        availableChoices(
          gift
        );

      const exact =
        available.find(
          choice => {
            const title =
              String(
                choice.title ||
                ''
              )
                .trim()
                .toLowerCase();

            const physical =
              title.includes(
                'fysisk'
              );

            const digital =
              title.includes('e-bog') ||
              title.includes('ebog') ||
              title.includes('e bog') ||
              title.includes('digital') ||
              title.includes('+');

            return (
              physical &&
              !digital
            );
          }
        );

      if (exact) {
        return exact;
      }

      if (
        available.length === 1
      ) {
        const choiceTitle =
          String(
            available[0].title ||
            ''
          )
            .trim()
            .toLowerCase();

        const giftTitle =
          String(
            gift.title ||
            ''
          )
            .trim()
            .toLowerCase();

        const combined =
          `${giftTitle} ${choiceTitle}`;

        const digital =
          combined.includes('e-bog') ||
          combined.includes('ebog') ||
          combined.includes('e bog') ||
          combined.includes('digital') ||
          combined.includes('+');

        if (
          !digital &&
          (
            choiceTitle ===
              'default title' ||
            giftTitle.includes(
              'fysisk'
            )
          )
        ) {
          return available[0];
        }
      }

      return null;
    };


  const giftProductIds =
    new Set();


  config.giftProducts
    .forEach(
      gift => {
        giftProductIds.add(
          Number(
            gift.productId
          )
        );

        choicesFor(
          gift
        )
          .forEach(
            choice => {
              giftProductIds.add(
                Number(
                  choice.productId
                )
              );
            }
          );
      }
    );


  const isGiftLine =
    item =>
      String(
        item?.properties
          ?._smartmakro_free_gift ||
        ''
      ) === 'true';


  const giftLines =
    cart =>
      (
        cart?.items ||
        []
      )
        .filter(
          isGiftLine
        );


  const eligibleSubtotal =
    cart =>
      (
        cart?.items ||
        []
      )
        .reduce(
          (
            sum,
            item
          ) => {
            if (
              isGiftLine(
                item
              )
            ) {
              return sum;
            }

            if (
              config.excludeGiftProductsFromThreshold &&
              giftProductIds.has(
                Number(
                  item.product_id
                )
              )
            ) {
              return sum;
            }

            return (
              sum +
              Number(
                item.original_line_price ??
                item.line_price ??
                0
              )
            );
          },
          0
        );


  const qualifies =
    cart =>
      eligibleSubtotal(
        cart
      ) >=
      Number(
        config.threshold ||
        0
      );


  const getSections =
    () => {
      const sections =
        [];

      try {
        document.documentElement
          .dispatchEvent(
            new CustomEvent(
              'cart:grouped-sections',
              {
                bubbles: true,

                detail: {
                  sections
                }
              }
            )
          );
      } catch (_) {}

      return [
        ...new Set(
          sections.filter(
            Boolean
          )
        )
      ];
    };


  const fetchCart =
    async () => {
      const response =
        await fetch(
          `${root}cart.js`,
          {
            credentials:
              'same-origin',

            cache:
              'no-store',

            headers: {
              Accept:
                'application/json'
            }
          }
        );

      if (
        !response.ok
      ) {
        throw new Error(
          'Kurven kunne ikke hentes.'
        );
      }

      return response.json();
    };


  const publishCart =
    (
      cart,
      sections = {}
    ) => {
      cart.sections =
        sections ||
        {};

      try {
        if (
          window.FoxTheme
            ?.pubsub
            ?.PUB_SUB_EVENTS
            ?.cartUpdate
        ) {
          FoxTheme.pubsub.publish(
            FoxTheme.pubsub
              .PUB_SUB_EVENTS
              .cartUpdate,
            {
              cart
            }
          );
        }
      } catch (error) {
        console.warn(
          'Smart Makro Free Gift: cart update fejlede.',
          error
        );
      }

      document.dispatchEvent(
        new CustomEvent(
          'smartmakro-free-gift:cart-updated',
          {
            detail: {
              cart
            }
          }
        )
      );
    };


  const syncCart =
    async (
      sections = {}
    ) => {
      const cart =
        await fetchCart();

      state.cart =
        cart;

      publishCart(
        cart,
        sections
      );

      render();

      return cart;
    };


  const removeGiftLines =
    async (
      cart,
      includeSections = true
    ) => {
      const lines =
        giftLines(
          cart
        );

      if (
        !lines.length
      ) {
        return {
          sections: {}
        };
      }

      const updates =
        {};

      lines.forEach(
        item => {
          updates[
            item.key
          ] = 0;
        }
      );

      const sections =
        includeSections
          ? getSections()
          : [];

      const payload = {
        updates
      };

      if (
        sections.length
      ) {
        payload.sections =
          sections;

        payload.sections_url =
          window.location.pathname;
      }

      const response =
        await fetch(
          `${root}cart/update.js`,
          {
            method:
              'POST',

            credentials:
              'same-origin',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            body:
              JSON.stringify(
                payload
              )
          }
        );

      const result =
        await response.json();

      if (
        !response.ok ||
        result.status
      ) {
        throw new Error(
          result.description ||
          result.message ||
          'Gratisgaven kunne ikke fjernes.'
        );
      }

      return result;
    };


  const addGift =
    async choice => {
      const sections =
        getSections();

      const payload = {
        items: [
          {
            id:
              Number(
                choice.variantId
              ),

            quantity: 1,

            properties: {
              _smartmakro_free_gift:
                'true'
            }
          }
        ]
      };

      if (
        sections.length
      ) {
        payload.sections =
          sections;

        payload.sections_url =
          window.location.pathname;
      }

      const response =
        await fetch(
          `${root}cart/add.js`,
          {
            method:
              'POST',

            credentials:
              'same-origin',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            body:
              JSON.stringify(
                payload
              )
          }
        );

      const result =
        await response.json();

      if (
        !response.ok ||
        result.status
      ) {
        throw new Error(
          result.description ||
          result.message ||
          'Gratisgaven kunne ikke tilføjes.'
        );
      }

      return result;
    };


  const giftIsFree =
    cart => {

      const giftLine =
        giftLines(
          cart
        )[0];

      if (!giftLine) {
        return false;
      }


      const variantId =
        Number(
          giftLine.variant_id
        );


      const matchingLines =
        (
          cart?.items ||
          []
        )
          .filter(
            item =>
              Number(
                item.variant_id
              ) ===
              variantId
          );


      if (
        !matchingLines.length
      ) {
        return false;
      }


      const unitPrice =
        Number(
          giftLine.original_price ??
          giftLine.price ??
          0
        );


      let totalDiscount =
        0;


      for (
        const item
        of matchingLines
      ) {

        const quantity =
          Math.max(
            1,
            Number(
              item.quantity ||
              1
            )
          );


        const originalLinePrice =
          Number(
            item.original_line_price ??
            (
              Number(
                item.original_price ??
                item.price ??
                0
              ) *
              quantity
            )
          );


        const finalLinePrice =
          Number(
            item.final_line_price ??
            item.line_price ??
            originalLinePrice
          );


        if (
          finalLinePrice === 0
        ) {
          return true;
        }


        const inferredDiscount =
          Math.max(
            0,
            originalLinePrice -
            finalLinePrice
          );


        const allocatedDiscount =
          (
            item.line_level_discount_allocations ||
            []
          )
            .reduce(
              (
                sum,
                allocation
              ) =>
                sum +
                Number(
                  allocation.amount ||
                  0
                ),
              0
            );


        totalDiscount +=
          Math.max(
            inferredDiscount,
            allocatedDiscount
          );

      }


      return (
        unitPrice > 0 &&
        totalDiscount >=
          unitPrice - 1
      );

    };


  const findDrawer =
    () =>
      document.querySelector(
        'cart-drawer'
      ) ||

      document.querySelector(
        '[data-cart-drawer]'
      ) ||

      document.querySelector(
        '.cart-drawer'
      );


  const createWidget =
    () => {
      const node =
        document.createElement(
          'div'
        );

      node.className =
        'smartmakro-free-gift';

      node.id =
        'SmartMakroFreeGiftWidget';

      node.style.setProperty(
        '--smfg-bg',
        config.background ||
        '#f5f8f4'
      );

      node.style.setProperty(
        '--smfg-accent',
        config.accent ||
        '#5fba57'
      );

      node.style.setProperty(
        '--smfg-progress',
        config.progress ||
        '#d63a2f'
      );

      node.style.setProperty(
        '--smfg-success',
        config.success ||
        '#43a047'
      );

      return node;
    };


  const mount =
    () => {

      let widget =
        document.getElementById(
          'SmartMakroFreeGiftWidget'
        );


      if (
        widget?.isConnected
      ) {
        return widget;
      }


      const drawer =
        findDrawer();


      if (!drawer) {
        return null;
      }


      widget =
        createWidget();


      const footer =
        drawer.querySelector(
          '[data-cart-footer], .cart-drawer__footer, .drawer__footer, .cart-drawer__bottom, .drawer__bottom'
        );


      if (
        footer?.parentNode
      ) {

        footer.parentNode
          .insertBefore(
            widget,
            footer
          );

      } else {

        const content =
          drawer.querySelector(
            '.cart-drawer__content, .drawer__inner, .drawer__content, form'
          );


        (
          content ||
          drawer
        )
          .appendChild(
            widget
          );

      }


      return widget;

    };


  const choiceMatchesCart =
    (
      choice,
      currentGift
    ) =>
      Boolean(
        currentGift &&

        Number(
          choice.variantId
        ) ===

        Number(
          currentGift.variant_id
        )
      );


  const giftSelected =
    (
      gift,
      currentGift
    ) =>
      choicesFor(
        gift
      )
        .some(
          choice =>
            choiceMatchesCart(
              choice,
              currentGift
            )
        );


  const directChoiceFor =
    gift => {
      if (
        gift.physicalOnly
      ) {
        return strictPhysicalChoice(
          gift
        );
      }

      const available =
        availableChoices(
          gift
        );

      return (
        available.length === 1
          ? available[0]
          : null
      );
    };


  const cardImageFor =
    (
      gift,
      currentGift
    ) => {
      const selected =
        choicesFor(
          gift
        )
          .find(
            choice =>
              choiceMatchesCart(
                choice,
                currentGift
              )
          );

      return (
        selected?.image ||
        gift.image ||
        availableChoices(
          gift
        )[0]?.image ||
        ''
      );
    };


  const renderChoicePanel =
    (
      gift,
      currentGift
    ) => {
      if (
        !gift ||
        gift.physicalOnly
      ) {
        return '';
      }

      const choices =
        choicesFor(
          gift
        );

      if (
        choices.length <= 1
      ) {
        return '';
      }

      const buttons =
        choices
          .map(
            choice => {
              const current =
                choiceMatchesCart(
                  choice,
                  currentGift
                );

              return `
                <button
                  type="button"
                  class="smartmakro-free-gift__variant-choice ${current ? 'is-current' : ''}"
                  data-smartmakro-choice="${Number(choice.variantId)}"
                  ${choice.available ? '' : 'disabled'}
                >

                  ${
                    choice.image
                      ? `
                          <span class="smartmakro-free-gift__variant-thumb">
                            <img
                              src="${esc(choice.image)}"
                              alt=""
                              loading="lazy"
                            >
                          </span>
                        `
                      : ''
                  }

                  <span class="smartmakro-free-gift__variant-name">
                    ${esc(choice.title)}
                  </span>

                  ${
                    choice.available
                      ? ''
                      : `
                          <span class="smartmakro-free-gift__soldout">
                            Udsolgt
                          </span>
                        `
                  }

                  ${
                    current
                      ? `
                          <span class="smartmakro-free-gift__variant-current">
                            ✓
                          </span>
                        `
                      : ''
                  }

                </button>
              `;
            }
          )
          .join('');

      return `
        <div class="smartmakro-free-gift__variant-panel">

          <div class="smartmakro-free-gift__variant-panel-head">

            <p>
              Vælg variant af ${formatGiftTitle(gift.title)}
            </p>

            <button
              type="button"
              class="smartmakro-free-gift__variant-close"
              data-smartmakro-choice-close
              aria-label="Luk"
            >
              ×
            </button>

          </div>

          <div class="smartmakro-free-gift__variant-choices">
            ${buttons}
          </div>

        </div>
      `;
    };


  /* =========================================================
     PRODUKTOVERLAY / QUICK VIEW
     ========================================================= */

  const infoModal =
    () =>
      document.getElementById(
        'SmartMakroGiftInfoModal'
      );


  const infoGift =
    () =>
      config.giftProducts
        .find(
          item =>
            Number(
              item.productId
            ) ===
            Number(
              state.infoGiftId
            )
        );


  const mediaForGift =
    gift => {
      if (
        Array.isArray(
          gift?.media
        ) &&
        gift.media.length
      ) {
        return gift.media
          .filter(
            item =>
              item &&
              (
                item.src ||
                item.html ||
                item.preview
              )
          );
      }

      const images =
        Array.isArray(
          gift?.images
        ) &&
        gift.images.length

          ? gift.images

          : gift?.image

            ? [
                gift.image
              ]

            : [];

      return images.map(
        src => ({
          type: 'image',
          src,
          preview: src,
          html: null
        })
      );
    };


  const stepInfoMedia =
    direction => {
      const gift =
        infoGift();

      if (!gift) {
        return;
      }

      const media =
        mediaForGift(
          gift
        );

      if (
        media.length <= 1
      ) {
        return;
      }

      state.infoImageIndex =
        (
          state.infoImageIndex +
          direction +
          media.length
        ) %
        media.length;

      renderInfoModal();
    };


  const closeInfoModal =
    () => {
      const modal =
        infoModal();

      if (!modal) {
        return;
      }

      modal.classList.remove(
        'is-open'
      );

      modal.setAttribute(
        'aria-hidden',
        'true'
      );

      document.documentElement
        .classList.remove(
          'smfg-info-open'
        );

      const content =
        modal.querySelector(
          '[data-smartmakro-info-content]'
        );

      if (content) {
        content.innerHTML =
          '';
      }

      state.infoGiftId =
        null;

      state.infoImageIndex =
        0;
    };


  const renderInfoModal =
    () => {
      const modal =
        infoModal();

      if (!modal) {
        return;
      }

      const gift =
        infoGift();

      if (!gift) {
        closeInfoModal();

        return;
      }

      const media =
        mediaForGift(
          gift
        );

      state.infoImageIndex =
        Math.max(
          0,
          Math.min(
            state.infoImageIndex,
            Math.max(
              media.length - 1,
              0
            )
          )
        );

      const activeMedia =
        media[
          state.infoImageIndex
        ] ||
        null;

      const isVideo =
        Boolean(
          activeMedia &&
          (
            activeMedia.type ===
              'video' ||

            activeMedia.type ===
              'external_video'
          )
        );

      const mainMedia =
        !activeMedia

          ? ''

          : (
              isVideo &&
              activeMedia.html
            )

            ? `
                <div class="smfg-product-modal__video">
                  ${activeMedia.html}
                </div>
              `

            : activeMedia.src

              ? `
                  <img
                    src="${esc(activeMedia.src)}"
                    alt="${esc(gift.title)}"
                  >
                `

              : activeMedia.preview

                ? `
                    <img
                      src="${esc(activeMedia.preview)}"
                      alt="${esc(gift.title)}"
                    >
                  `

                : '';


      const thumbs =
        media
          .map(
            (
              item,
              index
            ) => {
              const video =
                item.type ===
                  'video' ||

                item.type ===
                  'external_video';

              const preview =
                item.preview ||
                item.src ||
                gift.image ||
                '';

              return `
                <button
                  type="button"
                  class="smfg-product-modal__thumb ${index === state.infoImageIndex ? 'is-active' : ''}"
                  data-smartmakro-info-image="${index}"
                  aria-label="${video ? 'Vis produktvideo' : 'Vis produktbillede'} ${index + 1}"
                >

                  ${
                    preview
                      ? `
                          <img
                            src="${esc(preview)}"
                            alt=""
                            loading="lazy"
                          >
                        `
                      : ''
                  }

                  ${
                    video
                      ? `
                          <span
                            class="smfg-product-modal__thumb-play"
                            aria-hidden="true"
                          >
                            ▶
                          </span>
                        `
                      : ''
                  }

                </button>
              `;
            }
          )
          .join('');


      const content =
        modal.querySelector(
          '[data-smartmakro-info-content]'
        );

      if (!content) {
        return;
      }


      content.innerHTML = `
        <div class="smfg-product-modal__media">

          <div class="smfg-product-modal__main-image">

            ${mainMedia}

            ${
              media.length > 1
                ? `
                    <button
                      type="button"
                      class="smfg-product-modal__nav smfg-product-modal__nav--prev"
                      data-smartmakro-info-prev
                      aria-label="Forrige medie"
                    >
                      ‹
                    </button>

                    <button
                      type="button"
                      class="smfg-product-modal__nav smfg-product-modal__nav--next"
                      data-smartmakro-info-next
                      aria-label="Næste medie"
                    >
                      ›
                    </button>
                  `
                : ''
            }

          </div>

          ${
            media.length > 1
              ? `
                  <div class="smfg-product-modal__thumbs">
                    ${thumbs}
                  </div>
                `
              : ''
          }

        </div>


        <div class="smfg-product-modal__details">

          <span class="smfg-product-modal__eyebrow">
            GRATIS GAVE
          </span>

          <h2>
            ${formatGiftTitle(gift.title)}
          </h2>

          <div class="smfg-product-modal__price">

            <span>
              Normalpris: ${esc(gift.priceFormatted || '')}
            </span>

            ${
              gift.compareAtFormatted
                ? `
                    <span class="smfg-product-modal__compare">
                      ${esc(gift.compareAtFormatted)}
                    </span>
                  `
                : ''
            }

          </div>


          <div class="smfg-product-modal__description">
            ${
              gift.descriptionHtml ||
              '<p>Læs mere om produktet på produktsiden.</p>'
            }
          </div>


          ${
            choicesFor(
              gift
            ).length > 1 &&
            !gift.physicalOnly

              ? `
                  <p class="smfg-product-modal__variant-note">
                    Farve/variant vælger du direkte i gavevælgeren.
                  </p>
                `

              : ''
          }


          ${
            gift.url

              ? `
                  <a
                    class="smfg-product-modal__link"
                    href="${esc(gift.url)}"
                    target="_blank"
                    rel="noopener"
                  >
                    Se hele produktsiden →
                  </a>
                `

              : ''
          }

        </div>
      `;


      const activeVideo =
        content.querySelector(
          '.smfg-product-modal__main-image video'
        );

      if (activeVideo) {
        activeVideo.muted =
          true;

        activeVideo.defaultMuted =
          true;

        activeVideo.playsInline =
          true;

        activeVideo.loop =
          true;

        activeVideo
          .setAttribute(
            'muted',
            ''
          );

        activeVideo
          .setAttribute(
            'playsinline',
            ''
          );

        activeVideo
          .setAttribute(
            'autoplay',
            ''
          );

        activeVideo
          .play()
          .catch(
            () => {}
          );
      }
    };


  const openInfoModal =
    giftId => {
      const modal =
        infoModal();

      if (!modal) {
        return;
      }

      document.body
        .appendChild(
          modal
        );

      state.infoGiftId =
        Number(
          giftId
        );

      state.infoImageIndex =
        0;

      renderInfoModal();

      modal.classList.add(
        'is-open'
      );

      modal.setAttribute(
        'aria-hidden',
        'false'
      );

      document.documentElement
        .classList.add(
          'smfg-info-open'
        );

      window.setTimeout(
        () => {
          modal
            .querySelector(
              '[data-smartmakro-info-close]'
            )
            ?.focus();
        },
        20
      );
    };


  /* =========================================================
     RENDER GAVEVÆLGER
     ========================================================= */

  const render =
    () => {
      const widget =
        mount();

      if (
        !widget ||
        !state.cart
      ) {
        return;
      }

      const subtotal =
        eligibleSubtotal(
          state.cart
        );

      const threshold =
        Number(
          config.threshold ||
          0
        );

      const unlocked =
        subtotal >=
        threshold;

      const currentGift =
        giftLines(
          state.cart
        )[0] ||
        null;


      const giftPickerCollapsed =
        Boolean(
          unlocked &&
          currentGift &&
          !state.giftPickerExpanded
        );

      widget.classList.toggle(
        'is-collapsed',
        giftPickerCollapsed
      );

      widget.classList.toggle(
        'is-expanded',
        Boolean(
          currentGift &&
          state.giftPickerExpanded
        )
      );


      const currency =
        state.cart.currency ||
        'DKK';

      widget.classList.toggle(
        'is-loading',
        state.loading
      );


      /* =====================================================
         UNDER 499 KR.
         ===================================================== */

      if (!unlocked) {
        state.activeGiftId =
          null;

        state.giftPickerExpanded =
          false;

        const remaining =
          Math.max(
            0,
            threshold -
            subtotal
          );

        const progress =
          threshold > 0

            ? Math.min(
                100,
                Math.max(
                  0,
                  (
                    subtotal /
                    threshold
                  ) * 100
                )
              )

            : 100;

        const text =
          String(
            config.lockedText ||
            'Du mangler [amount] for at få en gratis gave'
          )
            .replace(
              '[amount]',
              money(
                remaining,
                currency
              )
            );

        widget.innerHTML = `
          <p class="smartmakro-free-gift__locked-text">
            ${esc(text)}
          </p>

          <div class="smartmakro-free-gift__progress">

            <span
              class="smartmakro-free-gift__progress-bar"
              style="width:${progress.toFixed(2)}%"
            ></span>

          </div>

          ${
            state.error
              ? `
                  <p class="smartmakro-free-gift__error is-visible">
                    ${esc(state.error)}
                  </p>
                `
              : ''
          }
        `;


        if (
          currentGift &&
          !state.autoRemoving &&
          !state.loading
        ) {
          state.autoRemoving =
            true;

          removeGiftLines(
            state.cart
          )
            .then(
              result =>
                syncCart(
                  result.sections ||
                  {}
                )
            )
            .catch(
              error => {
                state.error =
                  error.message ||
                  'Gratisgaven kunne ikke fjernes.';
              }
            )
            .finally(
              () => {
                state.autoRemoving =
                  false;

                render();
              }
            );
        }

        return;
      }


      /* =====================================================
         OVER 499 KR.
         ===================================================== */

      const heading =
        currentGift

          ? (
              config.selectedHeading ||
              'Din gratis gave er valgt!'
            )

          : (
              config.unlockedHeading ||
              'Du har låst en gratis gave op!'
            );


      const subtext =
        currentGift

          ? (
              config.selectedSubtext ||
              'Vi har tilføjet den til din kurv. Du kan stadig vælge en anden, hvis du ombestemmer dig.'
            )

          : (
              config.unlockedText ||
              'Vælg din favorit – den bliver automatisk gratis i kurven.'
            );


      const cards =
        config.giftProducts
          .map(
            gift => {
              const selected =
                giftSelected(
                  gift,
                  currentGift
                );

              const direct =
                directChoiceFor(
                  gift
                );

              const choices =
                choicesFor(
                  gift
                );

              const physicalMissing =
                gift.physicalOnly &&
                !direct;

              const hasChoiceStep =
                !gift.physicalOnly &&
                choices.length > 1;

              const active =
                Number(
                  state.activeGiftId
                ) ===
                Number(
                  gift.productId
                );

              const image =
                cardImageFor(
                  gift,
                  currentGift
                );

              const disabled =
                physicalMissing ||
                (
                  !direct &&
                  !hasChoiceStep
                );

              const cta =
                physicalMissing

                  ? 'Fysisk bog mangler'

                  : selected

                    ? (
                        config.selectedText ||
                        'Valgt'
                      )

                    : hasChoiceStep

                      ? 'Vælg variant'

                      : (
                          config.chooseText ||
                          'Vælg gave'
                        );


              return `
                <div
                  class="
                    smartmakro-free-gift__card-wrap
                    ${selected ? 'is-selected-wrap' : ''}
                    ${active ? 'is-choosing-wrap' : ''}
                  "
                >

                  <button
                    type="button"
                    class="
                      smartmakro-free-gift__option
                      ${selected ? 'is-selected' : ''}
                      ${active ? 'is-choosing' : ''}
                    "
                    data-smartmakro-gift-card="${Number(gift.productId)}"
                    ${disabled ? 'disabled' : ''}
                  >

                    <span class="smartmakro-free-gift__image-wrap">

                      ${
                        image
                          ? `
                              <img
                                class="smartmakro-free-gift__image"
                                src="${esc(image)}"
                                alt="${esc(gift.title)}"
                                loading="lazy"
                              >
                            `
                          : ''
                      }

                      <span class="smartmakro-free-gift__check">
                        ✓
                      </span>

                    </span>

                    <span class="smartmakro-free-gift__title">
                      ${formatGiftTitle(gift.title)}
                    </span>

                    <span class="smartmakro-free-gift__cta">
                      ${esc(cta)}
                    </span>

                  </button>


                  <button
                    type="button"
                    class="smartmakro-free-gift__read-more"
                    data-smartmakro-read-more="${Number(gift.productId)}"
                  >
                    Læs om produktet
                    <span aria-hidden="true">→</span>
                  </button>

                </div>
              `;
            }
          )
          .join('');


      const activeGift =
        config.giftProducts
          .find(
            gift =>
              Number(
                gift.productId
              ) ===
              Number(
                state.activeGiftId
              )
          );


      const summaryHtml =
        currentGift

          ? `
              <div
                class="smartmakro-free-gift__summary"
                data-smartmakro-gift-toggle
                role="button"
                tabindex="0"
                aria-expanded="${state.giftPickerExpanded ? 'true' : 'false'}"
              >
                <div class="smartmakro-free-gift__heading">
                  ${esc(heading)}
                </div>

                <div class="smartmakro-free-gift__subtext">
                  ${esc(subtext)}
                </div>
              </div>
            `

          : `
              <div class="smartmakro-free-gift__summary">
                <div class="smartmakro-free-gift__heading">
                  ${esc(heading)}
                </div>

                <div class="smartmakro-free-gift__subtext">
                  ${esc(subtext)}
                </div>
              </div>
            `;


      widget.innerHTML = `
        ${summaryHtml}

        <div class="smartmakro-free-gift__grid">
          ${cards}
        </div>

        ${
          renderChoicePanel(
            activeGift,
            currentGift
          )
        }

        ${
          state.error
            ? `
                <p class="smartmakro-free-gift__error is-visible">
                  ${esc(state.error)}
                </p>
              `
            : ''
        }
      `;
    };


  /* =========================================================
     VÆLG GAVE
     ========================================================= */

  const selectChoice =
    async choice => {
      if (
        state.loading ||
        !choice?.available
      ) {
        return;
      }

      try {
        state.loading =
          true;

        state.error =
          '';

        render();

        let cart =
          state.cart ||
          await fetchCart();


        if (
          !qualifies(
            cart
          )
        ) {
          state.error =
            'Kurven opfylder ikke længere beløbsgrænsen.';

          return;
        }


        const current =
          giftLines(
            cart
          )[0];


        if (
          current &&

          Number(
            current.variant_id
          ) ===

          Number(
            choice.variantId
          )
        ) {
          state.activeGiftId =
            null;

          state.giftPickerExpanded =
            false;

          render();

          return;
        }


        if (
          giftLines(
            cart
          ).length
        ) {
          await removeGiftLines(
            cart,
            false
          );

          cart =
            await fetchCart();
        }


        const addResult =
          await addGift(
            choice
          );


        cart =
          await syncCart(
            addResult.sections ||
            {}
          );


        state.activeGiftId =
          null;


        state.giftPickerExpanded =
          false;


        if (
          !giftIsFree(
            cart
          )
        ) {
          const removeResult =
            await removeGiftLines(
              cart
            );

          await syncCart(
            removeResult.sections ||
            {}
          );

          state.error =
            'Gaven blev ikke 0 kr. Kontrollér at gaveproduktet og alle tilladte farver/varianter er med i Shopify-rabatten.';
        }

      } catch (error) {
        console.error(
          'Smart Makro Free Gift:',
          error
        );

        state.error =
          error.message ||
          'Der opstod en fejl. Prøv igen.';

      } finally {
        state.loading =
          false;

        render();
      }
    };


  /* =========================================================
     CLICK EVENTS
     ========================================================= */

  document.addEventListener(
    'click',
    event => {


      /* COLLAPSE / EXPAND */

      const giftToggle =
        event.target.closest(
          '[data-smartmakro-gift-toggle]'
        );

      if (
        giftToggle
      ) {
        event.preventDefault();

        event.stopPropagation();

        state.giftPickerExpanded =
          !state.giftPickerExpanded;

        if (
          !state.giftPickerExpanded
        ) {
          state.activeGiftId =
            null;
        }

        render();

        return;
      }


      /* CLOSE INFO */

      const closeInfo =
        event.target.closest(
          '[data-smartmakro-info-close]'
        );

      if (
        closeInfo
      ) {
        event.preventDefault();

        closeInfoModal();

        return;
      }


      /* BACKDROP */

      const backdrop =
        event.target.closest(
          '[data-smartmakro-info-backdrop]'
        );

      if (
        backdrop &&
        event.target === backdrop
      ) {
        event.preventDefault();

        closeInfoModal();

        return;
      }


      /* PREVIOUS MEDIA */

      const prevMedia =
        event.target.closest(
          '[data-smartmakro-info-prev]'
        );

      if (
        prevMedia
      ) {
        event.preventDefault();

        event.stopPropagation();

        stepInfoMedia(
          -1
        );

        return;
      }


      /* NEXT MEDIA */

      const nextMedia =
        event.target.closest(
          '[data-smartmakro-info-next]'
        );

      if (
        nextMedia
      ) {
        event.preventDefault();

        event.stopPropagation();

        stepInfoMedia(
          1
        );

        return;
      }


      /* MEDIA THUMB */

      const imageButton =
        event.target.closest(
          '[data-smartmakro-info-image]'
        );

      if (
        imageButton
      ) {
        event.preventDefault();

        state.infoImageIndex =
          Number(
            imageButton.dataset
              .smartmakroInfoImage ||
            0
          );

        renderInfoModal();

        return;
      }


      /* LÆS OM PRODUKTET */

      const readMore =
        event.target.closest(
          '[data-smartmakro-read-more]'
        );

      if (
        readMore
      ) {
        event.preventDefault();

        event.stopPropagation();

        openInfoModal(
          Number(
            readMore.dataset
              .smartmakroReadMore
          )
        );

        return;
      }


      /* VARIANT */

      const choiceButton =
        event.target.closest(
          '[data-smartmakro-choice]'
        );

      if (
        choiceButton
      ) {
        event.preventDefault();

        event.stopPropagation();

        const variantId =
          Number(
            choiceButton.dataset
              .smartmakroChoice
          );

        const choice =
          config.giftProducts
            .flatMap(
              choicesFor
            )
            .find(
              item =>
                Number(
                  item.variantId
                ) ===
                variantId
            );

        selectChoice(
          choice
        );

        return;
      }


      /* LUK VARIANTPANEL */

      if (
        event.target.closest(
          '[data-smartmakro-choice-close]'
        )
      ) {
        event.preventDefault();

        event.stopPropagation();

        state.activeGiftId =
          null;

        render();

        return;
      }


      /* GAVEKORT */

      const card =
        event.target.closest(
          '[data-smartmakro-gift-card]'
        );

      if (
        !card
      ) {
        return;
      }

      event.preventDefault();

      event.stopPropagation();

      const gift =
        config.giftProducts
          .find(
            item =>
              Number(
                item.productId
              ) ===
              Number(
                card.dataset
                  .smartmakroGiftCard
              )
          );

      if (
        !gift
      ) {
        return;
      }

      const direct =
        directChoiceFor(
          gift
        );

      if (
        direct
      ) {
        state.activeGiftId =
          null;

        selectChoice(
          direct
        );

      } else {

        state.activeGiftId =
          Number(
            gift.productId
          );

        state.giftPickerExpanded =
          true;

        render();
      }
    }
  );


  /* =========================================================
     KEYBOARD
     ========================================================= */

  document.addEventListener(
    'keydown',
    event => {
      const modalOpen =
        infoModal()
          ?.classList
          .contains(
            'is-open'
          );

      if (!modalOpen) {
        return;
      }

      if (
        event.key ===
          'Escape'
      ) {
        closeInfoModal();

        return;
      }

      if (
        event.key ===
          'ArrowLeft'
      ) {
        event.preventDefault();

        stepInfoMedia(
          -1
        );

        return;
      }

      if (
        event.key ===
          'ArrowRight'
      ) {
        event.preventDefault();

        stepInfoMedia(
          1
        );
      }
    }
  );


  /* =========================================================
     SWIPE I QUICK VIEW
     ========================================================= */

  let infoTouchStartX =
    null;


  document.addEventListener(
    'touchstart',
    event => {
      const main =
        event.target.closest(
          '.smfg-product-modal__main-image'
        );

      if (
        !main ||

        !infoModal()
          ?.classList
          .contains(
            'is-open'
          )
      ) {
        return;
      }

      if (
        event.target.closest(
          'video, iframe'
        )
      ) {
        return;
      }

      infoTouchStartX =
        event.touches?.[0]
          ?.clientX ??
        null;
    },
    {
      passive: true
    }
  );


  document.addEventListener(
    'touchend',
    event => {
      if (
        infoTouchStartX ===
          null
      ) {
        return;
      }

      const endX =
        event.changedTouches?.[0]
          ?.clientX ??
        infoTouchStartX;

      const delta =
        endX -
        infoTouchStartX;

      infoTouchStartX =
        null;

      if (
        Math.abs(
          delta
        ) < 45
      ) {
        return;
      }

      stepInfoMedia(
        delta > 0
          ? -1
          : 1
      );
    },
    {
      passive: true
    }
  );


  /* =========================================================
     REFRESH CART
     ========================================================= */

  const refresh =
    async () => {
      try {
        state.cart =
          await fetchCart();

        render();
      } catch (error) {
        console.warn(
          'Smart Makro Free Gift: cart refresh fejlede.',
          error
        );
      }
    };


  const observer =
    new MutationObserver(
      () => {
        if (
          !document.getElementById(
            'SmartMakroFreeGiftWidget'
          )
        ) {
          mount();

          render();
        }
      }
    );


  const init =
    () => {
      observer.observe(
        document.body,
        {
          childList: true,
          subtree: true
        }
      );

      refresh();

      document.addEventListener(
        'product-ajax:added',
        refresh
      );

      document.addEventListener(
        'cart:updated',
        refresh
      );

      document.addEventListener(
        'cart:update',
        refresh
      );

      document.addEventListener(
        'shopify:section:load',
        refresh
      );

      try {
        if (
          window.FoxTheme
            ?.pubsub
            ?.subscribe &&

          FoxTheme.pubsub
            .PUB_SUB_EVENTS
            ?.cartUpdate
        ) {
          FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub
              .PUB_SUB_EVENTS
              .cartUpdate,

            event => {
              if (
                event?.cart
              ) {
                state.cart =
                  event.cart;

                render();
              } else {
                refresh();
              }
            }
          );
        }
      } catch (_) {}
    };


  if (
    document.readyState ===
      'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }

})();